import { useState, useEffect, useCallback } from "react";
import { getListingsByCategory } from "../../service/listing/listingService";

/**
 * Hook để lấy listings theo CategoryID (tối ưu cho CategoryPage)
 * @param {number} categoryId - ID của category
 * @param {number} pageSize - Số items mỗi trang
 */
export const useCategoryListings = (categoryId, pageSize = 20) => {
  const [listings, setListings] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true); // ✅ Set true by default để show skeleton
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch listings cho page cụ thể
  const fetchListings = useCallback(
    async (pageNum, reset = false) => {
      if (!categoryId) {
        setLoading(false);
        return;
      }

      // ✅ Chỉ set loading = true nếu là first load hoặc reset
      if (reset || pageNum === 1) {
        setLoading(true);
      }
      setError(null);

      try {
        const newItems = await getListingsByCategory(
          categoryId,
          pageNum,
          pageSize
        );

        if (reset || pageNum === 1) {
          setListings(newItems);
        } else {
          setListings((prev) => [...prev, ...newItems]);
        }

        setPage(pageNum + 1);
        setHasMore(newItems.length === pageSize);

        // Estimate total (nếu API không trả về total)
        if (reset || pageNum === 1) {
          setTotalCount(newItems.length);
        } else {
          setTotalCount((prev) => prev + newItems.length);
        }
      } catch (e) {
        console.error("Error fetching category listings:", e);
        setError("Không thể tải dữ liệu sản phẩm.");
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [categoryId, pageSize]
  );

  // Initial fetch khi categoryId thay đổi
  useEffect(() => {
    setListings([]);
    setPage(1);
    setHasMore(true);
    setTotalCount(0);
    fetchListings(1, true);
  }, [categoryId, fetchListings]);

  // Load more function
  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    fetchListings(page);
  }, [loading, hasMore, page, fetchListings]);

  // Refresh function
  const refresh = useCallback(() => {
    setListings([]);
    setPage(1);
    setHasMore(true);
    fetchListings(1, true);
  }, [fetchListings]);

  return {
    listings,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    totalCount,
  };
};
